import AddIcon from "@mui/icons-material/Add";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";

import {
  fieldSx,
  OUTIL_COLORS,
  paperSx,
  tableHeaderSx,
} from "./OutilContainer.styles";

export const ToolSectionHeader = ({ addDisabled = false, addTooltip, icon, onAdd, title, showAdd = true }) => (
  <Box
    sx={{
      alignItems: "center",
      backgroundColor: OUTIL_COLORS.panelHeader,
      borderBottom: `1px solid ${OUTIL_COLORS.border}`,
      display: "flex",
      gap: 0.75,
      justifyContent: "space-between",
      minHeight: 48,
      px: 1.75,
    }}
  >
    <Typography color={OUTIL_COLORS.navy} fontSize={12} fontWeight={700}>
      {icon && <Box component="span" sx={{ mr: 0.75 }}>{icon}</Box>}
      {title}
    </Typography>
    {showAdd && (
      <Tooltip title={addTooltip || (onAdd ? "Ajouter une entrée" : "Ajout disponible dans une prochaine version")}>
        <span>
          <Button
            disabled={!onAdd || addDisabled}
            onClick={onAdd}
            size="small"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: OUTIL_COLORS.gold,
              color: "#10263d",
              fontSize: 10,
              fontWeight: 700,
              textTransform: "none",
              "&.Mui-disabled": {
                backgroundColor: OUTIL_COLORS.gold,
                color: "#10263d",
                opacity: 0.8,
              },
              "&:hover": { backgroundColor: "#c79c2f" },
            }}
            variant="contained"
          >
            Ajouter
          </Button>
        </span>
      </Tooltip>
    )}
  </Box>
);

export const ToolHint = ({ children = "Cliquez sur une ligne pour ouvrir la fiche détail complète." }) => (
  <Box
    sx={{
      alignItems: "center",
      backgroundColor: "#f5f8fb",
      border: `1px solid ${OUTIL_COLORS.border}`,
      borderRadius: "6px",
      display: "flex",
      gap: 0.75,
      mx: 1.5,
      my: 1,
      px: 1.25,
      py: 0.8,
    }}
  >
    <LightbulbOutlinedIcon sx={{ color: "#d5ad3f", fontSize: 16 }} />
    <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>
      {children}
    </Typography>
  </Box>
);

export const EmptyToolTable = ({ columns, minWidth = 1100 }) => (
  <TableContainer sx={{ overflowX: "auto" }}>
    <Table aria-label="Tableau de l'outil" size="small" sx={{ minWidth }}>
      <TableHead>
        <TableRow>
          {columns.map((column) => (
            <TableCell key={column} sx={tableHeaderSx}>
              {column}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell align="center" colSpan={columns.length} sx={{ color: "#8091a3", fontSize: 11, py: 2 }}>
            Aucune entrée — la gestion des données sera disponible ultérieurement.
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </TableContainer>
);

const METADATA_FIELDS = [
  { key: "code", label: "Code/Réf" },
  { key: "version", label: "Version" },
  { key: "redige_par", label: "Rédigé par" },
  { key: "date_redaction", label: "Date rédaction", type: "date" },
  { key: "verifie_par", label: "Vérifié par" },
  { key: "approuve_par", label: "Approuvé par" },
];

export const DocumentPanel = ({ metadata, onChange, tool }) => (
  <Paper elevation={0} role="region" aria-label={`Fiche documentaire ${tool.label}`} sx={{ ...paperSx, overflow: "hidden" }}>
    <Box
      sx={{
        alignItems: { xs: "flex-start", md: "center" },
        backgroundColor: OUTIL_COLORS.panelHeader,
        borderBottom: `1px solid ${OUTIL_COLORS.border}`,
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        minHeight: 98,
        px: 2,
        py: 2,
      }}
    >
      <Typography color={OUTIL_COLORS.navy} fontSize={12} fontWeight={700} sx={{ minWidth: 230 }}>
        Outil {tool.number} : {tool.label}
        {tool.clause && (
          <Box component="span" sx={{ backgroundColor: "#dbe6f1", borderRadius: "999px", ml: 1, px: 0.8, py: 0.35 }}>
            {tool.clause}
          </Box>
        )}
      </Typography>
      <Tooltip title="Import disponible dans une prochaine version">
        <span>
          <Button
            disabled
            startIcon={<UploadFileOutlinedIcon />}
            sx={{
              border: "1px dashed #a9bfd4",
              color: OUTIL_COLORS.navy,
              justifyContent: "flex-start",
              minHeight: 56,
              px: 2,
              textAlign: "left",
              textTransform: "none",
              "&.Mui-disabled": { color: OUTIL_COLORS.navy, opacity: 0.72 },
            }}
            variant="outlined"
          >
            <Box>
              <Typography fontSize={12} fontWeight={700}>Importer un fichier Excel / CSV</Typography>
              <Typography fontSize={9.5}>Les colonnes seront mappées automatiquement dans une prochaine version.</Typography>
            </Box>
          </Button>
        </span>
      </Tooltip>
    </Box>
    <Box
      sx={{
        display: "grid",
        gap: 1.25,
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(6, minmax(0, 1fr))" },
        p: 2,
      }}
    >
      {METADATA_FIELDS.map((field) => (
        <TextField
          InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
          key={field.key}
          label={field.label}
          onChange={(event) => onChange(field.key, event.target.value)}
          size="small"
          sx={fieldSx}
          type={field.type || "text"}
          value={metadata[field.key] || ""}
        />
      ))}
    </Box>
  </Paper>
);

export const PlaceholderTool = ({ description, title }) => (
  <Paper elevation={0} role="region" aria-label={title} sx={{ ...paperSx, overflow: "hidden" }}>
    <ToolSectionHeader icon={<DescriptionOutlinedIcon sx={{ fontSize: 15, verticalAlign: "middle" }} />} showAdd={false} title={title} />
    <Box sx={{ alignItems: "center", display: "flex", flexDirection: "column", minHeight: 190, justifyContent: "center", p: 3, textAlign: "center" }}>
      <FileDownloadOutlinedIcon sx={{ color: "#8da1b5", fontSize: 34, mb: 1 }} />
      <Typography color={OUTIL_COLORS.navy} fontSize={13} fontWeight={700}>Structure prête pour intégration</Typography>
      <Typography color={OUTIL_COLORS.textMuted} fontSize={11} sx={{ maxWidth: 560, mt: 0.5 }}>
        {description}
      </Typography>
    </Box>
  </Paper>
);
